import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import Button from 'components/basic/Button/Button';
import { useInterval } from 'components/hooks';

import SliderDynamicStripes from './SliderDynamicStripes';
import { calculateQuickSelectValues } from './sliderUtils';

const Wrap = styled.div`
  /* +Text below */
  /* height: calc(26px + 20px); */
`;

const InnerWrap = styled.div<{
  $disabled: boolean;
}>`
  position: relative;
  width: 100%;
  height: 26px;
  line-height: 1;

  /* 4. NEW -- ui--slider
    https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/
  */

  /* reset */
  input[type='range'] {
    position: absolute;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    appearance: none;
    height: 26px;
    width: 100%;
    background: transparent !important;
    border: none !important;
    padding: 0;
    z-index: 50;
  }

  input[type='range']:focus {
    outline: none;
  }

  /* thumb */
  input[type='range']::-webkit-slider-thumb {
    position: relative;
    z-index: 5;
    appearance: none;
    height: 12px;
    width: 12px;
    border: 1px solid var(--main);
    border-radius: 2px;
    background: var(--black1b);
    box-shadow: 0 0 5px black;
    border-radius: 100%;
    cursor: pointer;

    border-radius: 4px;
    -webkit-appearance: none;
    background-clip: content-box;
  }

  /* Firefox thumb */
  input[type='range']::-moz-range-thumb {
    position: relative;
    z-index: 5;
    appearance: none;
    height: 12px;
    width: 12px;
    border: 1px solid #ff6a00;
    border-radius: 2px;
    background: var(--black1b);
    box-shadow: 0 0 5px black;
    border-radius: 100%;
    cursor: pointer;

    /* Magic needed to change circle Handle into square */
    border-radius: 4px;
    margin-top: -24px;
    background-clip: content-box;
  }

  /* input[type='range'] + label {
    position: absolute;
    left: 0;
    top: 20px;
    z-index: 0;
    width: 100%;
    height: 6px;
    cursor: pointer;
    background: #2b3138ee;
    border-radius: 0;
    padding: 0;
    pointer-events: none;
    span {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      background: var(--main);
    }
  } */
`;

function SelectedText({
  positionPercentage,
  displayValue,
  displaySign,
  middleWidthPercentage,
  hidden,
}: {
  positionPercentage: number;
  displayValue: number;
  displaySign: string;
  middleWidthPercentage: number;
  hidden: boolean;
}) {
  /**
   * Correct label based on %. Needs to be more to the right from 0% and corrected to the left near 100%.
   * Must be recalculated each px so that move is fluent.
   */
  const correctionPercentage =
    ((positionPercentage - 50) / 50) * (middleWidthPercentage / 2) +
    (middleWidthPercentage / 4) * (1 - positionPercentage / 100);
  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 2,
        top: 'calc(50% + 8px)',
        color: 'var(--mainLight)',
        left: `calc(${
          positionPercentage -
          middleWidthPercentage / 2 -
          correctionPercentage +
          2 /* px padding left correction */
        }%)`,
        userSelect: 'none',
        pointerEvents: 'none',
        fontSize: 12,
        opacity: hidden ? 0 : 1,
      }}
    >
      {displayValue}
      {displaySign}
    </div>
  );
}

// function Dot({
//   percentage,
//   current,
//   labelWidthInPercentage,
//   alwaysVisible,
//   hiddenText,
// }: {
//   percentage: number;
//   current: number;
//   labelWidthInPercentage: number;
//   alwaysVisible?: boolean;
//   hiddenText: boolean;
// }) {
//   const active = current > percentage;
//   const correctionPx = percentage * 0.1;
//   const GAP = 1;
//   return (
//     <div
//       style={{
//         position: 'absolute',
//         zIndex: 1,
//         top: 'calc(50% - 6px)',
//         left: `calc(${percentage}% - ${correctionPx}px - 1px)`,
//         height: 12,
//         width: 12,
//         borderRadius: 18,
//         background: active ? 'var(--main)' : '#2b3138ee',
//         border: '3px solid var(--black1b)',
//       }}
//     >
//       {(alwaysVisible ||
//         current > percentage + GAP + labelWidthInPercentage ||
//         current < percentage - GAP - labelWidthInPercentage) && (
//         <div
//           style={{
//             position: 'absolute',
//             top: 14,
//             left: `calc(-50% - ${
//               percentage === 0 ? -2 : percentage === 100 ? 20 : 4
//             }px)`,
//             userSelect: 'none',
//             color: '#9D98A4',
//             fontSize: 12,
//             opacity: hiddenText ? 0 : 1,
//             transition: 'opacity 0.3s',
//           }}
//         >
//           {percentage}%
//         </div>
//       )}
//     </div>
//   );
// }

const ShortcutWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 2px;
  padding: 8px 0;
  > div {
    flex: 1;
  }
`;

const Shortcut = ({
  value,
  valueSign,
  selected,
  onSelect,
}: {
  value: number;
  valueSign: string;
  selected: number;
  onSelect: (value: number) => void;
}) => {
  const isSelected = value === selected;
  return (
    <div>
      <Button
        onClick={() => onSelect(value)}
        color={isSelected ? 'black' : 'gray'}
        bordered
        minimalPadding
      >
        {value}
        {valueSign}
      </Button>
    </div>
  );
};

type Props = {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  quickSelectIncludeMin?: boolean;
  quickSelectSign?: string;
  hiddenLabel?: boolean;
  /** Inactive style means user needs to touch slider to make it active again */
  inactive?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
};

const SliderPercentage = ({
  value,
  min = 0,
  max = 100,
  quickSelectIncludeMin,
  quickSelectSign,
  onChange,
  disabled = false,
  inactive = false,
  onDragStart,
  onDragEnd,
  hiddenLabel,
}: Props) => {
  const [uuid] = useState(uuidv4());
  const dragStartedRef = useRef<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const [dragging, setDragging] = useState(false);
  const [widthPx, setWidthPx] = useState(0);

  useInterval(() => {
    if (ref.current && widthPx !== ref.current?.offsetWidth) {
      setWidthPx(ref.current?.offsetWidth);
    }
  }, 500);

  const characterWidthInPercentage = useMemo(() => {
    const pxToPercentage = 100 / widthPx;
    return 10 /* pixels */ * pxToPercentage;
  }, [widthPx]);

  // const zeroWidthPercentage = useMemo(
  //   () => Math.round(characterWidthInPercentage * 2),
  //   [characterWidthInPercentage]
  // );
  const middleWidthPercentage = useMemo(
    () => Math.round(characterWidthInPercentage * 3),
    [characterWidthInPercentage]
  );
  // const hundredWidthPercentage = useMemo(
  //   () => Math.round(characterWidthInPercentage * 4),
  //   [characterWidthInPercentage]
  // );
  const shortcutButtons = (
    <ShortcutWrap>
      {calculateQuickSelectValues(min, max, {
        includeMin: quickSelectIncludeMin,
      }).map(quickValue => (
        <Shortcut
          key={quickValue}
          selected={value}
          value={quickValue}
          valueSign={quickSelectSign ?? '%'}
          onSelect={onChange}
        />
      ))}
    </ShortcutWrap>
  );

  return (
    <Wrap>
      <InnerWrap ref={ref} $disabled={disabled}>
        {/* <Dot
        percentage={0}
        current={value}
        labelWidthInPercentage={zeroWidthPercentage}
        alwaysVisible={inactive}
        hiddenText={!dragging}
      />
      <Dot
        percentage={25}
        current={value}
        labelWidthInPercentage={middleWidthPercentage}
        hiddenText={!dragging}
      />
      <Dot
        percentage={50}
        current={value}
        labelWidthInPercentage={middleWidthPercentage}
        hiddenText={!dragging}
      />
      <Dot
        percentage={75}
        current={value}
        labelWidthInPercentage={middleWidthPercentage}
        hiddenText={!dragging}
      />
      <Dot
        percentage={100}
        current={value}
        labelWidthInPercentage={hundredWidthPercentage}
        hiddenText={!dragging}
      /> */}
        {!hiddenLabel && !inactive && (
          <SelectedText
            positionPercentage={
              max !== min ? ((value - min) / (max - min)) * 100 : 0
            }
            displayValue={value}
            displaySign={quickSelectSign ?? '%'}
            middleWidthPercentage={middleWidthPercentage}
            hidden={!dragging}
          />
        )}
        <input
          id={uuid}
          type="range"
          min={min}
          max={max}
          // step={1}
          disabled={disabled}
          value={value}
          style={{ padding: 0 }}
          onMouseDown={() => {
            onDragStart?.();
            dragStartedRef.current = true;
            // Prevent flash when clicking quickly without dragging
            setTimeout(() => {
              if (dragStartedRef.current) {
                setDragging(true);
              }
            }, 100);
          }}
          onMouseUp={() => {
            onDragEnd?.();
            dragStartedRef.current = false;
            setDragging(false);
          }}
          onChange={event => onChange(Number(event.target.value))}
          onTouchStart={onDragStart}
          onTouchEnd={onDragEnd}
        />
        {/* <SliderStripes /> */}
        <SliderDynamicStripes />
        {/* <label htmlFor={uuid}>
          <span
            style={{
              width: `$ {Math.floor(((value - min) / (max - min)) * 100)}%`,
            }}
          />
        </label> */}
      </InnerWrap>
      {shortcutButtons}
    </Wrap>
  );
};

export default SliderPercentage;
